import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Overlay } from "@/components/Overlay";
import { GameHeader, BetControls } from "@/components/games/GameShell";
import { api, errMsg, fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ROWS = 10;
const MULTS = {
  low: [8.9, 3, 1.4, 1.1, 1, 0.5, 1, 1.1, 1.4, 3, 8.9],
  medium: [22, 5, 2, 1.1, 0.6, 0.4, 0.6, 1.1, 2, 5, 22],
  high: [76, 10, 3, 0.9, 0.3, 0.2, 0.3, 0.9, 3, 10, 76],
};
const slotColor = (m) => (m >= 10 ? "#EF4444" : m >= 2 ? "#F5A524" : m >= 1 ? "#FFB800" : "#3B4252");

export function PlinkoModal({ game, onClose }) {
  const { currency, setUser } = useApp();
  const [risk, setRisk] = useState("medium");
  const [bet, setBet] = useState(currency === "gc" ? 100 : 0.5);
  useEffect(() => { setBet(currency === "gc" ? 100 : 0.5); }, [currency, setBet]);
  const [busy, setBusy] = useState(false);
  const [ball, setBall] = useState(null);
  const [history, setHistory] = useState([]);
  const [hot, setHot] = useState(null);
  const timers = useRef([]);
  const mults = MULTS[risk];

  const drop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/plinko", { currency, bet, risk, game_id: game.id });
      const path = data.path;
      const frames = path.reduce((a, b, r) => { a.push(50 + (sum(path.slice(0, r + 1)) - (r + 1) / 2) * 8); return a; }, [50]);
      setBall({ frames, slot: data.slot });
      timers.current.push(setTimeout(() => {
        setUser(data.user);
        setHot(data.slot);
        setHistory((h) => [{ id: `${Date.now()}-${Math.random()}`, m: data.multiplier, win: data.win, color: slotColor(data.multiplier) }, ...h].slice(0, 8));
        if (data.win > 0) toast.success(`${data.multiplier}× — won ${fmt(data.win, currency)} ${currency.toUpperCase()}`);
        setBall(null);
        setBusy(false);
        setTimeout(() => setHot(null), 900);
      }, ROWS * 160 + 300));
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <Overlay open={!!game} onClose={onClose} testId="plinko-modal" className="max-w-md">
      {game && (
        <div>
          <GameHeader game={game} />
          <div className="p-5">
            <div className="relative rounded-2xl bg-[#0B0E14] border border-white/5 p-4 overflow-hidden" data-testid="plinko-board" style={{ aspectRatio: "1 / 1" }}>
              {Array.from({ length: ROWS }).map((_, r) => (
                <div key={r} className="absolute left-0 right-0 flex justify-center gap-[6%]" style={{ top: `${8 + (r / ROWS) * 74}%` }}>
                  {Array.from({ length: r + 3 }).map((__, c) => <span key={c} className="w-1.5 h-1.5 rounded-full bg-white/40" />)}
                </div>
              ))}
              {ball && (
                <div
                  data-testid="plinko-ball"
                  className="absolute w-3 h-3 rounded-full bg-[#FFB800] shadow-[0_0_10px_#FFB800] z-10"
                  style={{ top: "4%", left: "50%" }}
                  ref={(el) => el && animateBall(el, ball.frames)}
                />
              )}
              <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-[2px] px-1">
                {mults.map((m, i) => (
                  <div key={i} data-testid={`plinko-slot-${i}`} className={`flex-1 rounded-md text-center py-1 text-[9px] sm:text-[10px] font-black text-[#0B0E14] transition-transform ${hot === i ? "-translate-y-1 scale-110" : ""}`} style={{ background: slotColor(m) }}>{m}×</div>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-1 overflow-x-auto no-scrollbar" data-testid="plinko-history">
              {history.map((h) => <span key={h.id} className="shrink-0 rounded-md px-2 py-1 text-xs font-bold text-[#0B0E14]" style={{ background: h.color }}>{h.m}×</span>)}
            </div>
            <div className="mt-4 flex rounded-full bg-[#0B0E14] p-1 border border-white/10 text-sm" data-testid="plinko-risk">
              {["low", "medium", "high"].map((r) => (
                <button key={r} data-testid={`plinko-risk-${r}`} disabled={busy} onClick={() => setRisk(r)} className={`flex-1 rounded-full py-2 font-bold capitalize transition-colors ${risk === r ? "bg-[#1F2633] text-white" : "text-gray-500"}`}>{r}</button>
              ))}
            </div>
            <div className="mt-4"><BetControls bet={bet} setBet={setBet} disabled={busy} /></div>
            <button data-testid="plinko-drop-btn" disabled={busy} onClick={drop} className="mt-4 w-full rounded-xl bg-[#FFB800] text-[#0B0E14] py-4 font-display text-xl font-black hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-[filter,transform,opacity]">
              {busy ? "Dropping…" : "DROP BALL"}
            </button>
          </div>
        </div>
      )}
    </Overlay>
  );
}

function sum(a) { return a.reduce((x, y) => x + y, 0); }

function animateBall(el, frames) {
  if (el.dataset.animated) return;
  el.dataset.animated = "1";
  let step = 0;
  const tick = () => {
    if (step >= frames.length) return;
    el.style.transition = "top 0.16s linear, left 0.16s ease-in-out";
    el.style.top = `${8 + (step / ROWS) * 74}%`;
    el.style.left = `${frames[step]}%`;
    step++;
    setTimeout(tick, 150);
  };
  tick();
}
